/**
 * Os pontos que precisam do perito (E152.F3, ADR-0103 §5.9): o que a versão em tela pede (face
 * proposta, contraprova apurada, suprimento do par, diligências decisivas abertas) e, quando o
 * perito pede para rever, a decisão em vigor. E172.F15: em PARTE, também o que o sistema apurou e
 * o assistente técnico pode revisar se julgar necessário (facultativa, sem segurar nada). Cada
 * ponto mostra a fonte por expansão e o formulário da revisão fundamentada. A lista é a do
 * servidor; a tela não inventa ponto.
 *
 * E180.F2 (GUS-254 itens 3 e 4, ADR-0133): nos dois modos, o ponto facultativo mostra o que o
 * sistema apurou e deixa o formulário atrás de "Revisar" — nada fica aberto esperando resposta.
 *
 * E184.F3 (GUS-145 item 4, ADR-0136 §3): a divergência apurada (o crédito, a integridade em PARTE e
 * a contraprova) mostra o valor lido e deixa o formulário atrás de "Aceitar com fundamento".
 */
import { useState, type ReactNode } from 'react';

import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ConferenciaFacialParecerData } from '@/lib/conferencia_facial';
import type {
  ClausulaPeloMotorData,
  AvaliacaoDaFormalizacaoData,
  RevisaoEmVigorData,
  RevisaoInData,
  TipoDaRevisao,
} from '@/lib/formalizacaoAvaliacao';

import { RevisaoFundamentada } from './RevisaoFundamentada';

const t = ptBR.formalizacaoAvaliacao;

interface Ponto {
  tipo: TipoDaRevisao;
  objeto: string;
  descricao: string;
  clausula?: ClausulaPeloMotorData | null | undefined;
  valor_lido?: string | undefined;
}

function Fonte({ a, ponto }: { a: AvaliacaoDaFormalizacaoData; ponto: Ponto }): JSX.Element {
  const elemento = a.elementos.find((e) => e.id === ponto.objeto);
  const par = a.rotas.find((r) => r.rota === 'SUP-F-3')?.elementos ?? [];
  const doPar = a.elementos.filter((e) => par.includes(e.id));
  const decisivas = a.diligencias.filter((d) => d.decisiva);
  return (
    <details className="text-body-sm">
      <summary className="cursor-pointer text-accent-700">{t.verFonte}</summary>
      <div className="mt-sc-2 space-y-sc-1 text-fg-muted">
        {elemento ? (
          <>
            <p className="text-fg">{elemento.fonte}</p>
            {elemento.alcance === ponto.descricao ? null : <p>{elemento.alcance}</p>}
          </>
        ) : null}
        {ponto.tipo === 'suprimento'
          ? doPar.map((e) => (
              <p key={e.id}>
                <span className="text-fg">{t.tipoDoElemento[e.tipo]}</span>: {e.fonte}
              </p>
            ))
          : null}
        {ponto.tipo === 'diligencias' ? (
          <ul className="list-disc pl-sc-6">
            {decisivas.map((d) => (
              <li key={d.id}>{d.descricao}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
}

/** O formulário do ponto facultativo, recolhido até o perito pedir para revisar. */
function Revisar({ children, rotulo }: { children: ReactNode; rotulo: string }): JSX.Element {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="space-y-sc-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-expanded={aberto}
        onClick={() => setAberto((a) => !a)}
        data-testid="formalizacao-revisar"
      >
        {aberto ? t.recolher : rotulo}
      </Button>
      {aberto ? children : null}
    </div>
  );
}

/** E184.F3: o que o motor leu na divergência que se aceita com fundamento; não muda com o aceite. */
function ValorLido({ valor }: { valor: string | undefined }): JSX.Element | null {
  if (!valor) return null;
  return (
    <p className="text-body-sm text-fg" data-testid="formalizacao-valor-lido">
      {t.valorLido.replace('{valor}', valor)}
    </p>
  );
}

/** E184.F3: a divergência apurada (com o valor lido) se aceita com fundamento; o resto, revisa. */
function rotuloDoRevisar(p: Ponto): string {
  return p.valor_lido ? t.aceitarComFundamento : t.revisar;
}

export interface PontosParaRevisaoProps {
  avaliacao: AvaliacaoDaFormalizacaoData;
  versao: number;
  rever: RevisaoEmVigorData | null;
  enviando: string | null;
  onRegistrar: (revisao: RevisaoInData) => Promise<boolean>;
  onCancelarRever: () => void;
  /** E172.F4 (B2.3): o parecer facial em vigor, para a verificação item a item da face. */
  parecerFacial?: ConferenciaFacialParecerData | null;
}

/** O sistema apurou o ponto (a cláusula não localizada é falta de material, não apuração). */
function apurado(a: AvaliacaoDaFormalizacaoData, p: Ponto): boolean {
  return a.elementos.find((e) => e.id === p.objeto)?.situacao !== 'ausente';
}

/** O parecer só acompanha o ponto da face que é dele (`face:<id>`). */
function parecerDoPonto(
  p: Ponto,
  parecer: ConferenciaFacialParecerData | null,
): ConferenciaFacialParecerData | null {
  return p.tipo === 'face' && parecer !== null && p.objeto === `face:${parecer.id}`
    ? parecer
    : null;
}

export function PontosParaRevisao({
  avaliacao,
  versao,
  rever,
  enviando,
  onRegistrar,
  onCancelarRever,
  parecerFacial = null,
}: PontosParaRevisaoProps): JSX.Element {
  const pontos: (Ponto & { rever: boolean; facultativa?: boolean })[] = [
    ...(rever
      ? [
          {
            tipo: rever.tipo,
            objeto: rever.objeto,
            descricao: t.reverPonto.replace('{decisao}', t.decisao[rever.decisao]),
            valor_lido: rever.valor_lido,
            rever: true,
          },
        ]
      : []),
    ...avaliacao.revisoes_pendentes.map((r) => ({ ...r, rever: false })),
    // E172.F15 (ADR-0128 §4): o que o sistema apurou sem revisão exigida; não é pendência
    ...(avaliacao.revisoes_facultativas ?? [])
      .filter((r) => r.objeto !== rever?.objeto)
      .map((r) => ({ ...r, rever: false, facultativa: true })),
  ];
  const formulario = (p: Ponto & { rever: boolean }): JSX.Element => (
    <RevisaoFundamentada
      tipo={p.tipo}
      objeto={p.objeto}
      versao={versao}
      enviando={enviando === p.objeto}
      onRegistrar={onRegistrar}
      parecerFacial={parecerDoPonto(p, parecerFacial)}
      clausula={p.clausula ?? null}
      {...(p.rever ? { onCancelar: onCancelarRever } : {})}
    />
  );
  return (
    <section aria-labelledby="formalizacao-pontos-titulo" data-testid="formalizacao-pontos">
      <h4 id="formalizacao-pontos-titulo" className="text-body-md font-semibold text-fg">
        {t.pontos}
      </h4>
      {pontos.length === 0 ? (
        <p className="text-body-sm text-fg-muted">{t.semPontos}</p>
      ) : (
        <ul className="mt-sc-2 space-y-sc-3">
          {pontos.map((p) => (
            <li
              key={`${p.rever ? 'rever' : p.facultativa ? 'facultativa' : 'pendente'}-${p.objeto}`}
              className="space-y-sc-2 border border-border p-sc-3"
              data-testid="formalizacao-ponto"
              data-tipo={p.tipo}
              data-facultativa={p.facultativa ? 'sim' : undefined}
            >
              <p className="text-body-md text-fg">
                {t.tipoDaRevisao[p.tipo]}{' '}
                {p.rever ? <Badge variant="outline">{t.rever}</Badge> : null}
                {p.facultativa ? <Badge variant="outline">{t.facultativa}</Badge> : null}
              </p>
              <p className="text-body-sm text-fg-muted">{p.descricao}</p>
              <ValorLido valor={p.valor_lido} />
              {p.facultativa ? (
                <p className="text-body-sm text-fg-muted">
                  {apurado(avaliacao, p) ? t.facultativaDescricao : t.facultativaSemApurado}
                </p>
              ) : null}
              <Fonte a={avaliacao} ponto={p} />
              {p.facultativa ? (
                <Revisar rotulo={rotuloDoRevisar(p)}>{formulario(p)}</Revisar>
              ) : (
                formulario(p)
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
